import { Download } from "lucide-react";
import { downloadFile } from "../lib/api";

const STATUS_STYLES = {
  SUBMITTED: "border-white/20 text-white/70",
  "UNDER REVIEW": "border-white/20 text-white/70",
  SOURCING: "border-volt/40 text-volt",
  "QUOTES RECEIVED": "border-volt/40 text-volt",
  "COMMERCIAL REVIEW": "border-volt/40 text-volt",
  "PROPOSAL SENT": "border-gold/50 text-gold",
  "CUSTOMER APPROVAL REQUIRED": "border-gold/60 text-gold bg-gold/10",
  APPROVED: "border-emerald-400/50 text-emerald-300",
  "SUPPLIER CONFIRMED": "border-emerald-400/50 text-emerald-300",
  FULFILLMENT: "border-emerald-400/50 text-emerald-300",
  DELIVERED: "border-emerald-400/60 text-emerald-300 bg-emerald-400/10",
  COMPLETED: "border-emerald-400/60 text-emerald-300 bg-emerald-400/10",
  CANCELLED: "border-red-400/40 text-red-300",
};

export function StatusChip({ status }) {
  return (
    <span
      data-testid={`status-chip-${(status || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`inline-block whitespace-nowrap text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${STATUS_STYLES[status] || "border-white/20 text-white/60"}`}
    >
      {status}
    </span>
  );
}

export function TypeChip({ type }) {
  const labels = {
    one_time: "One-Time",
    recurring: "Recurring",
    multi_location: "Multi-Location",
    international: "International",
    market_check: "Market Check",
  };
  return (
    <span className="inline-block text-[10px] tracking-[0.15em] uppercase border border-white/12 text-white/50 rounded-full px-3 py-1">
      {labels[type] || type}
    </span>
  );
}

export function Card({ title, action, children, className = "", testId }) {
  return (
    <div className={`glass-panel rounded-2xl p-6 md:p-8 ${className}`} data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-outfit text-lg font-light text-white">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Stat({ label, value, testId }) {
  return (
    <div className="glass-panel rounded-xl p-5" data-testid={testId}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-white/40">{label}</p>
      <p className="font-outfit text-3xl font-light text-white mt-2">{value}</p>
    </div>
  );
}

export function EmptyState({ title, text, children }) {
  return (
    <div className="text-center py-12">
      <p className="font-outfit text-xl font-light text-white/80">{title}</p>
      <p className="text-sm text-white/45 mt-2 max-w-sm mx-auto">{text}</p>
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}

export function FileList({ files = [] }) {
  if (!files.length) return <p className="text-sm text-white/40">No documents attached.</p>;
  return (
    <ul className="space-y-2" data-testid="file-list">
      {files.map((f, i) => (
        <li key={i} className="flex items-center justify-between text-sm bg-white/[0.03] border border-white/10 rounded-lg px-4 py-2.5">
          <span className="text-white/70 truncate max-w-[75%]">
            {f.original_filename}
            {f.upload_error && <span className="text-red-400 text-xs ml-2">(upload failed)</span>}
          </span>
          {f.storage_path && (
            <button
              data-testid={`file-download-${i}`}
              onClick={() => downloadFile(f.storage_path, f.original_filename)}
              className="text-white/40 hover:text-gold transition-colors"
              aria-label={`Download ${f.original_filename}`}
            >
              <Download className="w-4 h-4" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function Spinner() {
  return <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />;
}

export const inputCls =
  "w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

export function Field({ label, required, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-[11px] tracking-[0.2em] uppercase text-white/45 mb-2">
        {label}
        {required && <span className="text-gold"> *</span>}
      </span>
      {children}
    </label>
  );
}

export function fmtMoney(n, currency = "NGN") {
  const symbol = currency === "NGN" ? "₦" : currency === "USD" ? "$" : "";
  return `${symbol}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function ProgressBar({ delivered, ordered, testId }) {
  const pct = ordered > 0 ? Math.min(100, (delivered / ordered) * 100) : 0;
  return (
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-testid={testId}>
      <div
        className="h-full bg-gradient-to-r from-gold to-goldbright rounded-full transition-all duration-700"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
